import { ExternalLinkIcon, MenuIcon } from '@heroicons/react/outline';
import { css, DropdownMenu, IconButton, icons, keyframes, Portal, styled, } from '@showroomjs/ui';
import * as React from 'react';
import { audienceDefault } from '../lib/config';
import { isExternalLink } from '../lib/is-external-link';
import { NavLink, useNavigate } from '../lib/routing';
import { colorTheme, THEME } from '../theme';
import { AudienceDropdownGroup, AudienceToggle } from './audience-toggle';
import { Div } from './base';
import { GenericLink } from './generic-link';
const toggle = audienceDefault ? (React.createElement(Div, { css: { textAlign: 'right' } },
    React.createElement(AudienceToggle, null))) : null;
const navBarItems = THEME.navbar.items;
export const Sidebar = (props) => {
    const mobileSections = React.useMemo(() => {
        if (navBarItems) {
            return props.sections.concat({
                type: 'group',
                items: navBarItems
                    .filter((item) => item.showInMobileMenu)
                    .map((item) => ({
                    type: 'link',
                    href: item.to,
                    title: item.label,
                })),
                slug: '',
                title: '',
            });
        }
        return props.sections;
    }, [props.sections]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Div, { as: "nav", css: {
                display: 'none',
                '@md': {
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'sticky',
                    top: 'var(--header-height, 64px)',
                    left: 0,
                    bottom: 0,
                    height: 'calc(100vh - var(--header-height, 64px))',
                },
                borderRight: '1px solid',
                borderRightColor: '$gray-200',
                width: 240,
                background: '$gray-100',
                overflowY: 'auto',
                overscrollBehaviorBlock: 'contain',
            } },
            React.createElement(Div, { css: { flex: 1 } }, props.sections.map((section, i) => (React.createElement(SidebarSection, { section: section, key: i })))),
            toggle),
        React.createElement(MobileSidebar, { sections: mobileSections })));
};
const MobileSidebar = (props) => (React.createElement(Portal, { style: {
        position: 'fixed',
        top: 'auto',
        left: 'auto',
        right: 'calc(1rem + env(safe-area-inset-right, 24px))',
        bottom: 'calc(1rem + env(safe-area-inset-bottom, 24px))',
    }, className: colorTheme },
    React.createElement(DropdownMenu, null,
        React.createElement(DropdownMenu.Trigger, { asChild: true },
            React.createElement(IconButton, { css: {
                    width: 48,
                    height: 48,
                    backgroundColor: '$primary-700',
                    color: 'White',
                    boxShadow: '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',
                    '&:hover': {
                        backgroundColor: '$primary-900',
                    },
                    '@md': {
                        display: 'none',
                    },
                } },
                React.createElement(MenuIcon, { width: 24, height: 24 }))),
        React.createElement(DropdownMenu.Content, { className: colorTheme, css: {
                maxHeight: 'calc(100vh - env(safe-area-inset-bottom, 24px) - env(safe-area-inset-top, 24px) - 48px - 2rem)',
                maxWidth: 'calc(100vw - env(safe-area-inset-right, 24px) - env(safe-area-inset-left, 24px) - 2rem)',
                overflowY: 'auto',
            } },
            props.sections.map((section, i) => (React.createElement(DropdownSection, { section: section, key: i, index: i }))),
            audienceDefault ? React.createElement(AudienceDropdownGroup, null) : null))));
const DropdownSection = (props) => {
    const section = props.section;
    const { navigate } = useNavigate();
    switch (section.type) {
        case 'component':
            return section.hideFromSidebar ? null : (React.createElement(DropdownMenu.Item, { onSelect: () => navigate(`/${section.slug}`) }, section.title));
        case 'markdown':
            return section.hideFromSidebar ? null : (React.createElement(DropdownMenu.Item, { onSelect: () => navigate(`/${section.slug}`) }, section.formatLabel(section.frontmatter.title || section.fallbackTitle)));
        case 'link':
            return (React.createElement(DropdownMenu.Item, { asChild: true },
                React.createElement(DropdownLink, { href: section.href, css: { gap: '$2' } },
                    section.title,
                    isExternalLink(section.href) && (React.createElement(ExternalLinkIcon, { className: icons(), width: 20, height: 20 })))));
        case 'group':
            return section.hideFromSidebar ? null : (React.createElement(React.Fragment, null,
                props.index !== 0 && React.createElement(DropdownMenu.Separator, null),
                React.createElement(DropdownMenu.Label, null, section.title),
                section.items.map((sec, i) => (React.createElement(DropdownSection, { section: sec, key: i, index: i })))));
        default:
            return null;
    }
};
const SidebarSection = ({ section, level = 0, }) => {
    if ('slug' in section && section.slug === '') {
        return null;
    }
    switch (section.type) {
        case 'group':
            return section.hideFromSidebar ? null : (React.createElement(Div, { css: {
                    marginY: '$4',
                } },
                React.createElement(Div, { className: sectionClass() }, section.title),
                section.items.map((section, i) => (React.createElement(SidebarSection, { section: section, level: level + 1, key: i })))));
        case 'component':
            return section.hideFromSidebar ? null : (React.createElement(Div, { css: { px: '$2' } },
                React.createElement(Link, { to: `/${section.slug}`, root: level === 0, exact: true }, section.title)));
        case 'markdown':
            return section.hideFromSidebar ? null : (React.createElement(Div, { css: { px: '$2' } },
                React.createElement(Link, { to: `/${section.slug}`, root: level === 0, exact: true }, section.formatLabel(section.frontmatter.title || section.fallbackTitle))));
        case 'link':
            return (React.createElement(Div, { css: { px: '$2' } },
                React.createElement(Link, { href: section.href, target: "_blank", rel: "noopener", as: GenericLink, css: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '$2',
                        alignItems: 'center',
                        px: '$2',
                        borderRadius: '$md',
                    }, root: level === 0 },
                    section.title,
                    isExternalLink(section.href) && (React.createElement(ExternalLinkIcon, { className: icons(), width: 16, height: 16 })))));
        default:
            return null;
    }
};
const sectionClass = css({
    px: '$4',
    py: '$1',
    fontSize: '$xs',
    fontWeight: 'bold',
    letterSpacing: '0.05em',
    color: '$gray-400',
    textTransform: 'uppercase',
});
const DropdownLink = styled(GenericLink, {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});
const fadeInOut = keyframes({
    '0%': {
        opacity: 1,
    },
    '100%': {
        opacity: 0.6,
    },
});
const Link = styled(NavLink, {
    display: 'block',
    color: '$gray-600',
    textDecoration: 'none',
    px: '$2',
    py: '$1',
    borderRadius: '$md',
    fontSize: '$sm',
    lineHeight: '$sm',
    '&:hover': {
        backgroundColor: '$gray-200',
    },
    '&:focus': {
        outline: 'none',
    },
    '&:focus-visible': {
        outline: '1px solid $primary-300',
    },
    variants: {
        root: {
            true: {
                px: '$4',
                borderRadius: '$none',
            },
        },
    },
    '&[aria-current="page"]': {
        color: '$primary-900',
        backgroundColor: 'White',
    },
    '&[aria-busy]': {
        animation: `${fadeInOut} 500ms infinite alternate`,
    },
});
//# sourceMappingURL=sidebar.js.map